import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const HealthBadge = ({ ok, label }) => (
  <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${ok ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
    {ok ? '✓' : '✗'} {label}
  </span>
);

export default function TenantHealth() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [bulkCodes, setBulkCodes] = useState([]);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 4000); };

  const runHealthCheck = useCallback(async (activeOnly = false) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/health?active=${activeOnly}`);
      setReport(data.data);
    } catch (err) {
      showToast('Health check failed', 'error');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { runHealthCheck(false); }, [runHealthCheck]);

  const handleBulkAction = async (action) => {
    if (bulkCodes.length === 0) { showToast('Select tenants first', 'error'); return; }
    if (!window.confirm(`${action} ${bulkCodes.length} tenant(s)?`)) return;
    try {
      const { data } = await api.post('/bulk-action', { action, codes: bulkCodes });
      showToast(`${data.data.success} succeeded, ${data.data.failed} failed`);
      setBulkCodes([]);
      runHealthCheck(false);
    } catch (err) { showToast(err.response?.data?.message || 'Failed', 'error'); }
  };

  const toggleBulk = (code) => {
    setBulkCodes(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
  };

  const toggleAll = () => {
    if (!report?.tenants) return;
    if (bulkCodes.length === report.tenants.length) setBulkCodes([]);
    else setBulkCodes(report.tenants.map(t => t.code));
  };

  return (
    <div className="space-y-6">
      {toast && <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-xl shadow-lg text-sm font-medium ${toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'}`}>{toast.msg}</div>}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Tenant Health Monitor</h2>
          <p className="text-sm text-gray-500">Database connectivity, integrity checks & bulk operations</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => runHealthCheck(false)} disabled={loading}
            className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl text-sm font-medium shadow-sm disabled:opacity-50">
            {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : '🔍'}
            Run Health Check
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {report?.summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { label: 'Total', value: report.summary.total, color: 'bg-gray-900' },
            { label: 'Healthy', value: report.summary.healthy, color: 'bg-green-500' },
            { label: 'Degraded', value: report.summary.degraded, color: 'bg-amber-500' },
            { label: 'Offline', value: report.summary.offline, color: 'bg-red-500' },
            { label: 'Total Disk', value: `${report.summary.total_disk_mb} MB`, color: 'bg-blue-500' },
            { label: 'Avg Latency', value: `${report.summary.avg_latency_ms} ms`, color: 'bg-purple-500' },
          ].map(c => (
            <div key={c.label} className="bg-white rounded-xl border p-4 shadow-sm">
              <p className="text-xs font-medium text-gray-500">{c.label}</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{c.value}</p>
              <div className={`w-8 h-1 ${c.color} rounded mt-2`} />
            </div>
          ))}
        </div>
      )}

      {/* Bulk Actions Bar */}
      {bulkCodes.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-center justify-between">
          <span className="text-sm text-blue-800 font-medium">{bulkCodes.length} tenant(s) selected</span>
          <div className="flex gap-2">
            <button onClick={() => handleBulkAction('activate')} className="text-xs bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-700">Activate All</button>
            <button onClick={() => handleBulkAction('suspend')} className="text-xs bg-amber-600 text-white px-3 py-1.5 rounded-lg hover:bg-amber-700">Suspend All</button>
            <button onClick={() => setBulkCodes([])} className="text-xs bg-gray-200 text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-300">Clear</button>
          </div>
        </div>
      )}

      {/* Tenant Health Table */}
      {report?.tenants && (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="px-4 py-3 text-left">
                  <input type="checkbox" checked={bulkCodes.length === report.tenants.length && report.tenants.length > 0}
                    onChange={toggleAll} className="w-4 h-4 rounded" />
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Tenant</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Connectivity</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Objects</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Migrations</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Size</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Issues</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600"></th>
              </tr>
            </thead>
            <tbody>
              {report.tenants.map(t => {
                const isHealthy = t.connectivity && t.errors.length === 0;
                return (
                  <tr key={t.code} className={`border-b hover:bg-gray-50 ${!t.connectivity ? 'bg-red-50/50' : t.errors.length > 0 ? 'bg-amber-50/30' : ''}`}>
                    <td className="px-4 py-3"><input type="checkbox" checked={bulkCodes.includes(t.code)} onChange={() => toggleBulk(t.code)} className="w-4 h-4 rounded" /></td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{t.name}</div>
                      <div className="text-xs text-gray-400 font-mono">{t.code} &middot; {t.db_name}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${t.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{t.status}</span>
                    </td>
                    <td className="px-4 py-3">
                      {t.connectivity
                        ? <span className="text-green-600 text-xs font-medium">✓ {t.latency_ms}ms</span>
                        : <span className="text-red-600 text-xs font-medium">✗ Offline</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 flex-wrap">
                        <HealthBadge ok={t.tables.ok} label={`${t.tables.count}T`} />
                        <HealthBadge ok={t.views.ok} label={`${t.views.count}V`} />
                        <HealthBadge ok={t.stored_procedures.ok} label={`${t.stored_procedures.count}SP`} />
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {t.migration_history.exists
                        ? <span className="text-xs text-gray-600">{t.migration_history.applied} applied</span>
                        : <span className="text-xs text-red-500">No history</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">{t.disk_size_mb} MB</td>
                    <td className="px-4 py-3">
                      {isHealthy
                        ? <span className="text-green-600 text-xs">None</span>
                        : <span className="text-red-600 text-xs font-medium">{t.errors.length} issue(s)</span>}
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => setSelected(selected?.code === t.code ? null : t)} className="text-xs text-blue-600 hover:text-blue-800">
                        {selected?.code === t.code ? 'Hide' : 'Details'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Panel */}
      {selected && (
        <div className="bg-white rounded-xl border border-blue-200 p-5 shadow-sm">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="text-base font-semibold text-gray-900">{selected.name}</h3>
              <p className="text-xs text-gray-500 font-mono">{selected.code} &middot; {selected.db_name}</p>
            </div>
            <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600 text-lg">✕</button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">Tables</p>
              <p className="text-lg font-bold">{selected.tables.count} / {selected.tables.expected}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">Views</p>
              <p className="text-lg font-bold">{selected.views.count} / {selected.views.expected}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">Procedures</p>
              <p className="text-lg font-bold">{selected.stored_procedures.count} / {selected.stored_procedures.expected}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">Latency</p>
              <p className="text-lg font-bold">{selected.latency_ms} ms</p>
            </div>
          </div>

          {/* Seed Data */}
          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Seed Data Verification</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
            {Object.entries(selected.seed_data || {}).map(([table, info]) => (
              <div key={table} className={`text-xs px-3 py-2 rounded-lg border ${info.ok ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <span className="font-medium">{table}</span>: {info.count}/{info.expected}
                {info.error && <span className="text-red-500 ml-1">({info.error})</span>}
              </div>
            ))}
          </div>

          {/* Errors */}
          {selected.errors.length > 0 && (
            <>
              <h4 className="text-xs font-semibold text-red-500 uppercase mb-2">Issues Found</h4>
              <div className="space-y-1 mb-4">
                {selected.errors.map((e, i) => (
                  <div key={i} className="text-xs bg-red-50 border border-red-200 px-3 py-2 rounded-lg text-red-700">{e}</div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {!report && !loading && (
        <div className="bg-white rounded-xl border p-8 text-center text-gray-400">
          Click "Run Health Check" to scan all tenant databases.
        </div>
      )}
    </div>
  );
}
